import { Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { PushDestinationService } from '../../shared/services/push-destination/push-destination.service';
import { DeploymentInfoService } from '../../shared/services/deployment-info/deployment-info.service';
import {
  PushDestination,
  CreatePushDestinationRequest,
  UpdatePushDestinationRequest,
} from '../../shared/interfaces/push-destination.interface';

/**
 * Orchestration store for the push-destination dialog (component-provided).
 *
 * Owns everything EXCEPT the reactive FormGroup (that stays in the component):
 *  - `emailEnabled` gates the email service type, sourced from the global
 *    deployment-info (fetched once after auth, SCRUM-782) instead of a per-open
 *    project-scoped capabilities call. Email-configured is deployment-wide, so
 *    there's no reason to re-fetch it each time the dialog opens.
 *  - save orchestration, delegating to `PushDestinationService`.
 *
 * Provided at the component so its lifetime matches the dialog.
 */
@Injectable()
export class PushDestinationFormStore {
  readonly #service = inject(PushDestinationService);
  readonly #deploymentInfo = inject(DeploymentInfoService);

  readonly #projectId = signal<number | null>(null);

  /**
   * Whether the deployment has SMTP configured. Reads the global deployment-info
   * signal, which is `false` until deployment-info loads — preserving the old
   * "email stays disabled until known" behavior.
   */
  readonly emailEnabled = this.#deploymentInfo.emailEnabled;

  /** Set the project id used by create/update. */
  init(projectId: number): void {
    this.#projectId.set(projectId);
  }

  create(request: CreatePushDestinationRequest): Observable<PushDestination> {
    return this.#service.create(this.#projectId()!, request);
  }

  update(
    destinationId: number,
    request: UpdatePushDestinationRequest,
  ): Observable<PushDestination> {
    return this.#service.update(this.#projectId()!, destinationId, request);
  }
}
